import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import { useTranslation } from '../i18n';
import { PageHeader, Section } from '../components/Section';
import { formatCredits } from '../hooks/useCredits';
import { useAuth } from '../auth/AuthProvider';

type Package = { id: string; credits: number; usdAmount: number; bestValue: boolean };
type PaymentRow = {
  id: number;
  np_order_id: string;
  price_amount_usd: number;
  credits: number;
  status: string;
  pay_currency: string | null;
  invoice_url: string | null;
  created_at: string;
  updated_at: string;
};

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  created: 'default',
  waiting: 'info',
  confirming: 'info',
  confirmed: 'info',
  sending: 'info',
  partially_paid: 'warning',
  finished: 'success',
  failed: 'error',
  refunded: 'warning',
  expired: 'default',
};

export default function Payment() {
  const t = useTranslation();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const banner = params.get('status'); // 'success' | 'cancel' | null

  const [packages, setPackages] = useState<Package[]>([]);
  const [minUsd, setMinUsd] = useState(5);
  const [maxUsd, setMaxUsd] = useState(5000);
  const [customUsd, setCustomUsd] = useState<string>('');
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<PaymentRow[]>([]);

  useEffect(() => {
    void (async () => {
      const res = await fetch('/api/v1/payment/packages', { credentials: 'include' });
      if (res.ok) {
        const data = (await res.json()) as { packages: Array<{ id: string; credits: number; usdAmount: number; bestValue: boolean }>; min_usd: number; max_usd: number };
        setPackages(data.packages);
        setMinUsd(data.min_usd);
        setMaxUsd(data.max_usd);
      }
    })();
  }, []);

  const refreshHistory = async () => {
    const res = await fetch('/api/v1/payment/list', { credentials: 'include' });
    if (res.ok) {
      const data = (await res.json()) as { items: PaymentRow[] };
      setHistory(data.items);
    }
  };

  useEffect(() => {
    void refreshHistory();
    const id = setInterval(() => void refreshHistory(), 15_000);
    return () => clearInterval(id);
  }, []);

  const customCredits = useMemo(() => {
    const n = Number(customUsd);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }, [customUsd]);

  const handlePurchase = async (body: Record<string, unknown>, key: string) => {
    setError(null);
    setSubmitting(key);
    try {
      const res = await fetch('/api/v1/payment/invoice', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.message || `error: ${res.status}`);
      const invoiceUrl = (data as { invoice_url?: string }).invoice_url;
      if (!invoiceUrl) throw new Error('invoice_url missing in response');
      window.location.href = invoiceUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <Box>
      <PageHeader title={t.payment.title} description={t.payment.description} />

      {banner === 'success' && <Alert severity="info" sx={{ mb: 3 }}>{t.payment.successBanner}</Alert>}
      {banner === 'cancel' && <Alert severity="warning" sx={{ mb: 3 }}>{t.payment.cancelBanner}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {!user?.email && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              href="/account/recovery"
            >
              {t.payment.emailReminderLink}
            </Button>
          }
        >
          <Box sx={{ fontWeight: 600 }}>{t.payment.emailReminderTitle}</Box>
          <Box>{t.payment.emailReminderBody}</Box>
        </Alert>
      )}

      <Section title={t.payment.packagesTitle}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
          {packages.map((pkg) => (
            <Paper
              key={pkg.id}
              sx={{
                p: 3,
                borderColor: pkg.bestValue ? 'primary.main' : 'divider',
                borderWidth: pkg.bestValue ? 2 : 1,
                borderStyle: 'solid',
                position: 'relative',
              }}
            >
              {pkg.bestValue && (
                <Chip
                  size="small"
                  color="primary"
                  label={t.payment.bestValue}
                  sx={{ position: 'absolute', top: 12, right: 12 }}
                />
              )}
              <Typography sx={{ fontSize: 32, fontWeight: 700 }}>
                {formatCredits(pkg.credits)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t.payment.creditsLabel}
              </Typography>
              <Typography sx={{ mt: 1.5, fontSize: 18, fontWeight: 600 }}>
                ${pkg.usdAmount}
              </Typography>
              <Button
                variant={pkg.bestValue ? 'contained' : 'outlined'}
                fullWidth
                sx={{ mt: 2 }}
                disabled={submitting !== null}
                onClick={() => void handlePurchase({ package_id: pkg.id }, pkg.id)}
              >
                {submitting === pkg.id ? <CircularProgress size={16} /> : t.payment.payButton}
              </Button>
            </Paper>
          ))}
        </Box>
      </Section>

      <Section title={t.payment.customTitle}>
        <Paper sx={{ p: 3, maxWidth: 480 }}>
          <Stack spacing={1.5}>
            <TextField
              label={t.payment.customLabel}
              type="number"
              size="small"
              value={customUsd}
              onChange={(e) => setCustomUsd(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{ min: minUsd, max: maxUsd, step: 1 }}
              helperText={t.payment.customMin}
            />
            {customCredits > 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                = {formatCredits(customCredits)} {t.payment.creditsLabel}
              </Typography>
            )}
            <Button
              variant="contained"
              disabled={
                submitting !== null ||
                Number(customUsd) < minUsd ||
                Number(customUsd) > maxUsd
              }
              onClick={() => void handlePurchase({ custom_usd: Number(customUsd) }, 'custom')}
            >
              {submitting === 'custom' ? <CircularProgress size={16} /> : t.payment.payButton}
            </Button>
          </Stack>
        </Paper>
      </Section>

      <Section title={t.payment.historyTitle}>
        <Paper sx={{ overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ pl: 3 }}>Created</TableCell>
                <TableCell>Order</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Credits</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ pr: 3 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    {t.payment.historyEmpty}
                  </TableCell>
                </TableRow>
              ) : (
                history.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell sx={{ pl: 3, whiteSpace: 'nowrap' }}>
                      {new Date(p.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: 'text.secondary' }}>
                      {p.np_order_id.slice(0, 8)}…
                    </TableCell>
                    <TableCell align="right">${Number(p.price_amount_usd).toFixed(2)}</TableCell>
                    <TableCell align="right">{formatCredits(p.credits)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t.payment.statusLabels[p.status as keyof typeof t.payment.statusLabels] ?? p.status}
                        color={STATUS_COLOR[p.status] ?? 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ pr: 3 }} align="right">
                      {p.invoice_url && (
                        <Button
                          size="small"
                          endIcon={<OpenInNewIcon fontSize="small" />}
                          href={p.invoice_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t.payment.openInvoice}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Section>
    </Box>
  );
}
